import { type FlatContext, type LoggedEvent, LoggingEventType } from '@snapchat/logging';
import { polyfilledStructuredClone } from '@snapchat/polyfills';

import type { LoggingContext, LoggingCustomEvents } from '../loggingTypes';

// =============================================================================
// Utility functions and constants for Graphene Event Listeners.
// These are in a separate file so we can treeshake client and server dependencies
// Only add dependencies that should be shared between the client and server listeners to this file.
// =============================================================================

export const logFlushIntervalMs = 5e3;

export const silencedEventTypes = new Set([
  LoggingEventType.INFO,
  LoggingEventType.DEBUG,
  LoggingEventType.USER_ACTION,
  LoggingEventType.CUSTOM,
]);

// These are overrides from the default ones for legacy reasons.
export const getMetricName = (event: LoggedEvent<LoggingCustomEvents>): string | null => {
  switch (event.type) {
    case LoggingEventType.TIMING: {
      return 'app_measure_event';
    }

    case LoggingEventType.VALUE: {
      return 'app_measure_event';
    }

    case LoggingEventType.ERROR: {
      if (event.component === 'Csp') {
        return 'csp_error';
      }

      if (event.component === 'Contentful') {
        return 'contentful_error';
      }
    }
  }

  // Fallback to defaults for other events.
  return null;
};

export const getAdditionalDimensions = (
  context: Partial<LoggingContext>
): FlatContext | undefined => ({
  // only for csp violations, will not be populated for others.
  blockedUri: context.blockedUri,
  sourceFile: context.sourceFile,
  violatedDirective: context.violatedDirective,

  // only for contentful errors, may not be populated for others.
  errorCode: context.errorCode,
  errorName: context.errorName,

  // All others
  region: context.region, // Useful for graphene charting
  locale: context.locale, // Useful for debugging
  uaBrand: context.uaBrand,
  uaPlatform: context.uaPlatform,
});

/**
 * EventRewrite helper for Graphene Event Listeners.
 *
 * Used to replace high cardinality event porperties with static values.
 */
export const eventRewrite = (event: LoggedEvent<LoggingCustomEvents>) => {
  // LogPageLoad Component
  if (
    (event.type === LoggingEventType.TIMING || event.type === LoggingEventType.VALUE) &&
    event.component === 'NavigationTiming'
  ) {
    const output = polyfilledStructuredClone(event);
    output.label = '[REDACTED]'; // Includes current path
    return output;
  }

  // LogPageLoad business profile logs
  if (event.type === LoggingEventType.INFO && event.component === 'PersonaMatch') {
    const output = polyfilledStructuredClone(event);
    output.label = '[REDACTED]'; // Includes current path
    return output;
  }

  // initWebVitalsLogging()
  if (
    (event.type === LoggingEventType.TIMING || event.type === LoggingEventType.VALUE) &&
    event.component === 'WebVitals'
  ) {
    const output = polyfilledStructuredClone(event);
    output.label = '[REDACTED]'; // Includes current path
    return output;
  }

  // Video component
  if (event.type === LoggingEventType.TIMING && event.component === 'Video') {
    const output = polyfilledStructuredClone(event);
    output.label = '[REDACTED]'; // user defined analytics label
    return output;
  }

  // Default: no mutations, return original object
  return event;
};
